import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

export const WASH_TYPES = [["interno", "Interno"], ["esterno", "Esterno"], ["completo", "Completo"]];
export const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const blank = { wash_date: "", vehicle_id: "", place: "", wash_type: "completo", cost: "", notes: "", station_id: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const WashDialog = ({ item, stations, vehicles, isAdmin, places, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) setF({ ...blank, wash_date: today(), station_id: stations[0]?.id || "", ...item, cost: item.cost ?? "" });
  }, [item, stations]);
  if (!item) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const vOpts = vehicles.filter((v) => !isAdmin || v.station_id === f.station_id);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...Object.fromEntries(Object.keys(blank).map((k) => [k, f[k]])), cost: f.cost === "" ? null : Number(f.cost) };
      if (item.id) await api.put(`/washes/${item.id}`, payload); else await api.post("/washes", payload);
      toast.success("Lavaggio salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="wash-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? "Modifica lavaggio" : "Nuovo lavaggio"}</DialogTitle>
          <DialogDescription>Registra targa, data e dove è stato fatto il lavaggio.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {isAdmin && !item.id && <F label="Stazione" span>
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, vehicle_id: "" }))}>
              <SelectTrigger data-testid="wash-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          <F label="Targa mezzo"><PlateCombobox value={f.vehicle_id} onChange={(v) => setF((p) => ({ ...p, vehicle_id: v }))} testId="wash-vehicle-select" options={vOpts.map((v) => ({ value: v.id, label: v.plate }))} /></F>
          <F label="Data lavaggio"><Input type="date" max={today()} value={f.wash_date} onChange={set("wash_date")} data-testid="wash-date-input" /></F>
          <F label="Dove è stato effettuato" span>
            <Input value={f.place} onChange={set("place")} list="wash-places" placeholder="Es. Autolavaggio Rossi, Via Roma" data-testid="wash-place-input" />
            <datalist id="wash-places">{places.map((p) => <option key={p} value={p} />)}</datalist>
          </F>
          <F label="Tipo di lavaggio">
            <Select value={f.wash_type} onValueChange={(v) => setF((p) => ({ ...p, wash_type: v }))}>
              <SelectTrigger data-testid="wash-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{WASH_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Costo (€)"><Input type="number" step="0.01" min="0" value={f.cost} onChange={set("cost")} placeholder="Facoltativo" data-testid="wash-cost-input" /></F>
          <F label="Note" span><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="wash-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !f.wash_date || !f.vehicle_id || !f.place.trim()} data-testid="wash-save-button">{busy ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
